'use client';

import { FC, MouseEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  RedditShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';
import { Icon, IconSvg } from '@/components';
import { useIsServerSide } from '@/hooks';
import styles from './sharing.module.scss';

export type SocialNetworkType =
  | 'facebook'
  | 'twitter'
  | 'pinterest'
  | 'whatsapp'
  | 'linkedin'
  | 'reddit';

interface SharingProps {
  url: string;
  media?: string;
  include?: SocialNetworkType[];
  className?: string;
  iconStyle?: object;
  title?: string;
  text?: string;
  /** Force traditional sharing buttons instead of Web Share API */
  forceTraditional?: boolean;
}

const DEFAULT_SHARING_OPTIONS = ['FACEBOOK', 'TWITTER', 'WHATSAPP'];

const Sharing: FC<SharingProps> = ({
  url,
  media,
  include = DEFAULT_SHARING_OPTIONS,
  className,
  iconStyle,
  title = 'Check this out!',
  text = '',
  forceTraditional = false,
}) => {
  const [canUseWebShare, setCanUseWebShare] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const { isServerSide } = useIsServerSide();

  useEffect(() => {
    // Check if Web Share API is available in the browser
    const hasWebShareAPI =
      typeof navigator !== 'undefined' &&
      'share' in navigator &&
      !forceTraditional;

    // Enable Web Share API if available, regardless of device type
    setCanUseWebShare(hasWebShareAPI);

    // Show fallback buttons only when Web Share API is not available
    setShowFallback(!hasWebShareAPI);
  }, [forceTraditional]);

  /**
   * Check if a specific social network should be included in the sharing options
   */
  const isIncluded = (net: SocialNetworkType) =>
    !include || include.includes(net);

  /**
   * Handle sharing using the native Web Share API
   * Falls back to traditional buttons if the API fails
   */
  const handleNativeShare = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!navigator.share) {
      // Web Share API not available, show fallback buttons
      setShowFallback(true);
      return;
    }

    try {
      await navigator.share({
        title,
        text: text || title,
        url,
      });
    } catch (error) {
      // User cancelled the share dialog (AbortError) - do nothing
      if (error instanceof Error && error.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Error sharing:', error);
        // Real error occurred, show fallback buttons
        setShowFallback(true);
      }
    }
  };

  const commonProps = {
    resetButtonStyle: false,
    url,
  };

  // Prevent rendering on server-side to avoid hydration mismatches
  if (isServerSide) {
    return null;
  }

  // Render Web Share API button (works on both mobile and compatible desktop browsers)
  if (canUseWebShare && !showFallback) {
    return (
      <div
        className={classNames(styles.sharing, styles.sharing_native, className)}
      >
        <button
          aria-label="Share"
          className={styles.sharing_native_button}
          onClick={handleNativeShare}
          style={iconStyle}
        >
          <IconSvg name="share" />
        </button>
      </div>
    );
  }

  // Render traditional social media share buttons (fallback for unsupported browsers)
  return (
    <div className={classNames(styles.sharing, 'sharing', className)}>
      {isIncluded('facebook') && (
        <FacebookShareButton {...commonProps} aria-label="Share on Facebook">
          <Icon name="facebook" style={iconStyle} />
        </FacebookShareButton>
      )}
      {isIncluded('twitter') && (
        <TwitterShareButton {...commonProps} aria-label="Share on Twitter">
          <Icon name="twitter" style={iconStyle} />
        </TwitterShareButton>
      )}
      {isIncluded('pinterest') && (
        <PinterestShareButton
          {...commonProps}
          aria-label="Share on Pinterest"
          media={media as string}
        >
          <Icon name="pinterest" style={iconStyle} />
        </PinterestShareButton>
      )}
      {isIncluded('whatsapp') && (
        <WhatsappShareButton {...commonProps} aria-label="Share on WhatsApp">
          <Icon name="whatsapp" style={iconStyle} />
        </WhatsappShareButton>
      )}
      {isIncluded('reddit') && (
        <RedditShareButton {...commonProps} aria-label="Share on Reddit">
          <Icon name="reddit" style={iconStyle} />
        </RedditShareButton>
      )}
      {isIncluded('linkedin') && (
        <LinkedinShareButton {...commonProps} aria-label="Share on LinkedIn">
          <Icon name="linkedin" style={iconStyle} />
        </LinkedinShareButton>
      )}
    </div>
  );
};

export default Sharing;
